import type { ComponentType } from 'react';
import type { Channel, ChannelFilters, ChannelOptions, Channel as StreamChannel } from 'stream-chat';
import {
  ChannelList,
  type ChannelPreviewUIComponentProps,
  Chat,
  InfiniteScroll,
  useChatContext,
} from 'stream-chat-react';

import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import FontIcon from '@/shared/ui/FontIcon';
import { posthogCapture } from '@/shared/vendor/posthog';
import { useWiwDispatch, useWiwSelector } from '@/store';
import Loading from '@/workchat/components/Loading';
import { WORKCHAT_VIEWS } from '@/workchat/constants';
import Splash from '@/workchat/containers/Splash';
import { useStreamProvider } from '@/workchat/v2/providers/StreamProvider';
import { setConversation, toggleShow, viewStackPush } from '@/workchat/v2/store/reducer';
import { getWorkchatConnected, getWorkchatInitError } from '@/workchat/v2/store/selectors';
import ConversationListRowV3 from '@/workchat/v3/components/ConversationListV3/ConversationListItem/ConversationListItem';
import HeaderV3, { type HeaderButton } from '@/workchat/v3/components/HeaderV3/HeaderV3';

import '@/workchat/v3/components/ConversationListV3/ConversationListV3.scss';
import { useLDFlag } from '@/data/LD/selectors/getLDFlag';
import { openSendMessageDialog } from '@/teamtxt/dialogs';

const options: ChannelOptions = {
  limit: 10,
};

const sortChannels = (channels: StreamChannel[]) => {
  return channels.sort((channelA, channelB) => {
    // Show Broadcast channel first
    if (channelA.type === 'broadcast' && channelB.type !== 'broadcast') {
      return -1;
    }
    if (channelB.type === 'broadcast' && channelA.type !== 'broadcast') {
      return 1;
    }

    // Otherwise, sort by created_at, last_message_updated_at descending

    if (channelB.state.last_message_at && channelA.state.last_message_at) {
      return channelB.state.last_message_at.getTime() - channelA.state.last_message_at.getTime();
    }

    if (channelB.state.last_message_at) {
      return channelB.state.last_message_at.getTime() - new Date(channelA.data!.created_at as string).getTime();
    }

    if (channelA.state.last_message_at) {
      return new Date(channelB.data!.created_at as string).getTime() - channelA.state.last_message_at.getTime();
    }

    return 0;
  });
};

export default function ConversationListV3() {
  const dispatch = useWiwDispatch();
  const user = useWiwSelector(getAuthUser);

  const account = useWiwSelector(getAuthAccount);
  const connected = useWiwSelector(getWorkchatConnected);
  const initError = useWiwSelector(getWorkchatInitError);

  const { init, client } = useStreamProvider();
  const { setActiveChannel } = useChatContext();

  const teamTextEnabled = useLDFlag('FDT-1353-team-text');

  const canCreateNewConversation = !!account.settings?.workchat?.customChannelsAllowed;
  const canBroadcast = user.canSupervise();

  const filters: ChannelFilters = {
    members: { $in: [`${user.id}`] },
  };

  const newConversation = () => {
    if (!connected) {
      return;
    }
    dispatch(viewStackPush(WORKCHAT_VIEWS.VIEW_CREATE_NEW_CONVERSATION));
  };

  const chooseConversation = (channel: Channel) => {
    if (!connected || !channel) {
      return;
    }

    setActiveChannel(channel);
    // Set the selected conversation ID in the store
    dispatch(setConversation(channel.id));
    dispatch(viewStackPush(WORKCHAT_VIEWS.VIEW_CONVERSATION));
    posthogCapture('WorkChat: Viewed Channel', { accessType: 'Channel List' });
  };

  const ChannelPreview: ComponentType<ChannelPreviewUIComponentProps> = (props: ChannelPreviewUIComponentProps) => (
    <ConversationListRowV3
      key={props.channel.id}
      {...props}
      onConversationClick={() => chooseConversation(props.channel)}
    />
  );

  const renderConversationListContent = () => {
    if (!client || initError) {
      return (
        <Loading
          hideHeader
          hasError={!!initError}
          errorLabel="Something went wrong loading WorkChat"
          onErrorRetry={() => init?.()}
        />
      );
    }

    return (
      <Chat client={client} theme="str-chat__theme-dark">
        <ChannelList
          filters={filters}
          sort={[{ globally_pinned: 1 }, { last_updated: -1 }, { last_message_at: -1 }]}
          options={options}
          channelRenderFilterFn={sortChannels}
          Paginator={InfiniteScroll}
          onChannelVisible={event => {
            console.log(event);
          }}
          onChannelDeleted={event => {
            console.log(event);
          }}
          Preview={ChannelPreview}
        />
      </Chat>
    );
  };

  const buttons: HeaderButton[] = canCreateNewConversation
    ? [
        {
          label: <FontIcon icon="compose" />,
          className: 'green compose-button',
          action: newConversation,
          side: 'right',
          disabled: !connected || !client || !!initError,
          tooltip: 'New Message or Channel',
        },
      ]
    : [];

  const onTeamTxtClick = () => {
    dispatch(toggleShow(false));
    dispatch(openSendMessageDialog());
  };

  if (canBroadcast) {
    buttons.push({
      label: teamTextEnabled ? 'Send Email/TeamTXT' : 'Send Email',
      color: 'secondary',
      action: onTeamTxtClick,
      side: 'left',
    });
  }

  return (
    <div className="ConversationListV3">
      <HeaderV3 borderBottom buttons={buttons} />
      <div className="conversation-list-container">
        <Splash user={user} account={account} />
        {renderConversationListContent()}
      </div>
    </div>
  );
}
